import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { updatePasswordMutation } from '@/mutations/user.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { useFormik } from 'formik';
import { analytics } from '@/lib/analytics';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const ChangePasswordDialog: FunctionComponent<{ userId: string }> = ({ userId }) => {
  const { dismissActiveDialog } = useDialog();
  const { t } = useTranslation('account');

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validate: (values) => {
      const errors: any = {};
      if (values.newPassword !== values.confirmPassword) {
        errors.confirmPassword = t('changePassword.passwordsDoNotMatch');
      }
      if (values.newPassword.length < 8) {
        errors.newPassword = t('changePassword.passwordMinLength');
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const toastId = toast.loading(t('changePassword.changingPassword'));
      try {
        await execute(updatePasswordMutation, {
          id: userId,
          input: {
            currentPassword: _values.currentPassword,
            newPassword: _values.newPassword,
          },
        });
        analytics.capture('Password Changed');
        toast.success(t('changePassword.passwordChanged'), { id: toastId });
      } catch (error) {
        toast.error(t('changePassword.failedChangePassword'), { id: toastId });
      }
      dismissActiveDialog();
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='font-medium leading-none'>{t('changePassword.title')}</h3>
        <p className='text-sm font-medium leading-none text-slate-500'>{t('changePassword.description')}</p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='currentPassword'>{t('changePassword.currentPassword')}</Label>
          <Input
            id='currentPassword'
            name='currentPassword'
            type='password'
            value={formik.values.currentPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='newPassword'>{t('changePassword.newPassword')}</Label>
          <Input
            id='newPassword'
            name='newPassword'
            type='password'
            value={formik.values.newPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.newPassword && <p className='text-sm text-red-600'>{formik.errors.newPassword}</p>}
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmPassword'>{t('changePassword.confirmNewPassword')}</Label>
          <Input
            id='confirmPassword'
            name='confirmPassword'
            type='password'
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.errors.confirmPassword && <p className='text-sm text-red-600'>{formik.errors.confirmPassword}</p>}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            {t('changePassword.cancel')}
          </Button>
          <Button type='submit' disabled={formik.isSubmitting}>
            {t('changePassword.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
};
